import React from "react";
import DownloadList, { downloadsFor } from "../components/DownloadList";
import Gallery from "../components/Gallery";
import GroupCard from "../components/GroupCard";
import MediaImage from "../components/MediaImage";
import OfferingCard from "../components/OfferingCard";
import config from "../config";
import { emailHref } from "../data/offerings";
import { CURRENCY_NOTE } from "../data/practice";
import useContent, { useSiteSettings } from "../hooks/useContent";
import "./Offerings.css";

const CTA_VERB = { book: "book", register: "register" };

/** "Note text " before a fixed sentence, or nothing when the note is hidden. */
const notePrefix = (note) => (note ? `${note} ` : "");

/** The group card the monthly journals sit under (seed slug). */
const SUNDAY_STUDIO_SLUG = "sunday-studio";

/** Her monthly Sunday Studio journals (`downloads`, placement "sunday_studio"). */
function MonthlyJournals({ items }) {
  if (!items.length) return null;
  return (
    <div className="offering-journals">
      <h4>Monthly journals</h4>
      <DownloadList items={items} />
    </div>
  );
}

/**
 * Free groups (incl. Sunday Studio and its monthly journals), the groups'
 * photo album, Art & Exhale workshops and private groups: the owner-editable
 * `groups`, `downloads` (placement "sunday_studio"), `gallery` (album
 * "groups"), `workshops`, `private_groups` and settings `notes` (bundled copy
 * until the API answers).
 */
export default function GroupsWorkshops({ now = new Date() }) {
  const email = config.business.contactEmail;
  const { items: groups } = useContent("groups");
  const { items: workshops } = useContent("workshops");
  const { items: privateGroups } = useContent("private_groups");
  const { items: downloads } = useContent("downloads");
  const { notes } = useSiteSettings();
  const journals = downloadsFor(downloads, "sunday_studio");
  return (
    <section className="section white offerings-page">
      <div className="container">
        <h1>Groups &amp; Workshops</h1>
        <p className="muted currency-note">{CURRENCY_NOTE}</p>

        <section aria-labelledby="groups-heading" className="offering-section">
          <h2 id="groups-heading">Groups &amp; Community</h2>
          <div className="services offering-grid">
            {groups.map((g) => (
              <GroupCard key={g.slug} id={g.slug} group={g.data} fallback={notes.group_cycle_fallback} now={now}>
                {g.slug === SUNDAY_STUDIO_SLUG ? <MonthlyJournals items={journals} /> : null}
              </GroupCard>
            ))}
          </div>
          <p className="offering-note">
            {notePrefix(notes.group_cycle_note)}If there is a group you would like to see offered, please{" "}
            <a className="text-link" href={emailHref("Group suggestion")}>reach out via email</a>.
          </p>
          <Gallery album="groups" heading="From the groups" headingLevel={3} />
        </section>

        <section aria-labelledby="workshops-heading" className="offering-section">
          <h2 id="workshops-heading">Workshops</h2>
          <div className="services offering-grid">
            {workshops.map(({ slug, data: w }) => (
              <OfferingCard key={slug} id={slug} title={w.title} meta={w.price_lines}>
                {w.image ? <MediaImage media={w.image} className="offering-image" /> : null}
                {(w.paragraphs || []).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </OfferingCard>
            ))}
          </div>
          <p className="offering-note">
            {notePrefix(notes.workshop_note)}If there is a workshop you would like to see offered again, please{" "}
            <a className="text-link" href={emailHref("Workshop suggestion")}>reach out via email</a>.
          </p>
        </section>

        <section aria-labelledby="private-groups-heading" className="offering-section">
          <h2 id="private-groups-heading">Private Group Experiences</h2>
          <div className="services offering-grid">
            {privateGroups.map(({ slug, data: g }) => (
              <OfferingCard
                key={slug}
                id={slug}
                title={g.title}
                meta={g.price_lines}
                cta={
                  <p>
                    Email <a className="text-link" href={emailHref(`${g.title} inquiry`)}>{email}</a> to{" "}
                    {CTA_VERB[g.cta] || CTA_VERB.book}.
                  </p>
                }
              >
                {(g.paragraphs || []).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </OfferingCard>
            ))}
          </div>
        </section>
      </div>
    </section>
  );
}
